import type { AppearancePreferences, AppearanceTheme } from '@molt/shared';
import type { MenuSection } from '@/components/Menu';
import { sourceInterfaceMessage } from '@/lib/keyboard-shortcuts';
import type { InterfaceLanguageContextValue } from '@/lib/interface-language';

type InterfaceTranslator = InterfaceLanguageContextValue['t'];

export interface AppearanceMenuState {
  preferences: AppearancePreferences | null;
  loading?: boolean;
  themePending?: boolean;
  colourBlindModePending?: boolean;
  t?: InterfaceTranslator | undefined;
  onThemeChange: (theme: AppearanceTheme) => void;
  onColourBlindModeChange: (enabled: boolean) => void;
}

const THEME_LABEL_KEYS: Record<AppearanceTheme, string> = {
  light: 'appearance.light',
  dark: 'appearance.dark',
  match_system: 'appearance.matchSystem',
};

const THEME_ORDER: readonly AppearanceTheme[] = ['light', 'dark', 'match_system'];

export function appearanceMenuSections(state: AppearanceMenuState): MenuSection[] {
  const preferences = state.preferences ?? {
    theme: 'match_system' as const,
    colourBlindMode: false,
  };
  const t = state.t ?? sourceInterfaceMessage;
  const disabled = state.loading === true || state.themePending === true;
  const colourBlindDisabled =
    state.loading === true || state.colourBlindModePending === true;

  return [
    {
      id: 'appearance-theme',
      heading: t('appearance.heading'),
      selection: 'radio',
      value: preferences.theme,
      onValueChange: (id) => {
        if (id === 'light' || id === 'dark' || id === 'match_system') {
          state.onThemeChange(id);
        }
      },
      items: THEME_ORDER.map((theme) => ({
        id: theme,
        label: t(THEME_LABEL_KEYS[theme]),
        disabled,
        keepOpenOnSelect: true,
        onSelect: () => undefined,
      })),
    },
    {
      id: 'appearance-labels',
      items: [
        {
          id: 'colour-blind-label-mode',
          label: preferences.colourBlindMode
            ? t('appearance.colourBlindOn')
            : t('appearance.colourBlindOff'),
          disabled: colourBlindDisabled,
          keepOpenOnSelect: true,
          onSelect: () => state.onColourBlindModeChange(!preferences.colourBlindMode),
        },
      ],
    },
  ];
}
